import React, { useCallback, useEffect, useState } from 'react';
import { Button, Switch } from 'src/@vymo/ui/atoms';
import TextArea from 'src/@vymo/ui/atoms/textArea';
import Modal, { Body, Footer, Header } from 'src/@vymo/ui/blocks/modal';
import { FeatureDefinition } from '@growthbook/growthbook';
import { isJsonString } from 'src/workspace/utils';
import { featureFlagKeys, growthbook, GrowthBookFeaturesFlag } from './index';
import styles from './styles.module.scss';

type FlagKey = keyof GrowthBookFeaturesFlag;
type FlagValue = GrowthBookFeaturesFlag[FlagKey];

const GROWTHBOOK_OVERRIDE_KEY = 'growthbook-overrides';

function ValueCell({
  value,
  onFlagChange,
  flagKey,
}: {
  value: any;
  onFlagChange: (key: FlagKey, value: any) => void;
  flagKey: FlagKey;
}): JSX.Element {
  if (value === undefined || value === null) return <span />;

  if (typeof value === 'boolean') {
    return (
      <Switch
        value={value}
        onChange={(checked) => onFlagChange(flagKey, checked)}
      />
    );
  }

  const stringValue =
    typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value);

  return (
    <TextArea
      classNames={styles.value_text_area}
      value={stringValue}
      onChange={(updatedValue) => {
        try {
          const newValue = isJsonString(updatedValue)
            ? JSON.parse(updatedValue)
            : updatedValue;
          onFlagChange(flagKey, newValue);
        } catch (error) {
          // eslint-disable-next-line no-console
          console.warn(error);
        }
      }}
    />
  );
}

export default function FeatureFlagDevTool() {
  const [showModal, setShowModal] = useState(false);
  const [flags, setFlags] = useState<Record<FlagKey, FlagValue>>({} as any);
  const [originalFlags, setOriginalFlags] = useState<
    Record<FlagKey, FlagValue>
  >({} as any);

  useEffect(() => {
    let evaluated: Partial<GrowthBookFeaturesFlag> = {};
    if (localStorage.getItem(GROWTHBOOK_OVERRIDE_KEY)) {
      const stringifiedValues =
        localStorage.getItem(GROWTHBOOK_OVERRIDE_KEY) ?? `{""}`;
      evaluated = isJsonString(stringifiedValues)
        ? JSON.parse(stringifiedValues)
        : stringifiedValues;
    } else {
      featureFlagKeys.forEach((key) => {
        const value = growthbook.getFeatureValue(key, undefined);
        if (value !== undefined) {
          evaluated[key] = value;
        }
      });
    }
    setFlags(evaluated as Record<FlagKey, FlagValue>);
    setOriginalFlags(evaluated as Record<FlagKey, FlagValue>);
  }, []);

  const onFlagChange = useCallback((key: FlagKey, value: any) => {
    setFlags((prev) => ({ ...prev, [key]: value }));
  }, []);

  const onSave = () => {
    localStorage.setItem(GROWTHBOOK_OVERRIDE_KEY, JSON.stringify(flags));

    const features = Object.entries(flags).reduce((acc, [key, value]) => {
      acc[key] = { defaultValue: value };
      return acc;
    }, {} as Record<string, FeatureDefinition<any>>);

    growthbook.setFeatures(features);
    window.location.reload();
  };

  const onDiscard = () => {
    localStorage.removeItem(GROWTHBOOK_OVERRIDE_KEY);
    setFlags(originalFlags);

    const url = new URL(window.location.href);
    url.searchParams.delete('devtools');
    window.history.replaceState({}, '', url.toString());
    window.location.reload();
  };

  const closeModal = () => {
    setShowModal(false);
  };

  return showModal ? (
    <Modal
      classNames={styles.modal}
      onClose={closeModal}
      open={showModal}
      onBackdropClick={closeModal}
    >
      <Header>GrowthBook Feature Flags</Header>
      <Body>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Flag</th>
              <th>Value</th>
            </tr>
          </thead>
          <tbody>
            {featureFlagKeys.map((key) => (
              <tr key={key}>
                <td>{key}</td>
                <td>
                  <ValueCell
                    value={flags[key]}
                    flagKey={key}
                    onFlagChange={onFlagChange}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Body>
      <Footer>
        <Button onClick={onSave}>Save</Button>
        <Button variant="subtle" onClick={onDiscard}>
          Reset Flag
        </Button>
      </Footer>
    </Modal>
  ) : (
    <Button
      type="outlined"
      className={styles.fab}
      onClick={() => setShowModal(true)}
    >
      ⚙️ Growthbook DevTools
    </Button>
  );
}
